import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import { getArrayLength, getArrayOfEmails, isEmail } from 'utils';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import TextareaEmailsField from 'components/TextareaEmailsField';
import { Button } from 'ncoded-component-library';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { Form, Field } from 'react-final-form';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import { prepareTeamsOptions } from './utils';
import TeamsField, {
  MAX_NUM_OF_TEAMS,
} from 'components/TeamsField/TeamsField.component';
import ResolveMultiSelectField from 'components/ResolveMultiSelectField';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';
import PlusIcon from 'icons/Plus.icon';
import DataRoomTeam from './components/DataRoomTeam';

import './AddSellSideColleague.styles.scss';

type AddSellSideColleagueForm = {
  sellSideMembers: string[];
  sellSideMembersUnsubmited: string;
  addNewTeam: boolean;
  teamNames?: string[];
};

type AddSellSideColleagueProps = {
  className?: string;
};

const AddSellSideColleague: React.ForwardRefRenderFunction<
  SideDrawerRef,
  AddSellSideColleagueProps
> = (props, ref) => {
  const { className } = props;

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const { t } = useTranslation();

  const classes = classNames('add-sell-side-colleague', className);

  const { popServerError } = useContext(PopNotificationsContext);
  const {
    isAdmin,
    doesntHaveAccess,
    dataRoom: { _id, teams },
    setDataRoom,
  } = useContext(DataRoomContext);
  const { allMembers, setBuySideGroups, setSellSideMembers } =
    useContext(MembersContext);

  const teamSelectOptions = useCreateSelectOptions()(teams);

  const teamsLength = getArrayLength(teams);

  const canAddNewTeam = useMemo(
    () => isAdmin && teamsLength < MAX_NUM_OF_TEAMS,
    [isAdmin, teamsLength],
  );

  const initialValues = useMemo(
    () => ({
      addNewTeam: true,
      sellSideMembers: [],
      teamNames: [],
    }),
    [],
  );

  const renderTeams = useCallback(
    (teams: string[]) =>
      teamsLength ? (
        <div className="saved-teams">
          {prepareTeamsOptions(teams, allMembers).map(({ name, members }) => (
            <DataRoomTeam key={name} name={name} members={members} />
          ))}
        </div>
      ) : null,
    [allMembers, teamsLength],
  );

  const onInviteMembers = useCallback(
    async (values: AddSellSideColleagueForm) => {
      const {
        sellSideMembers: sellSideMembersVal,
        sellSideMembersUnsubmited,
        teamNames,
      } = values;

      const hasTeamNames = getArrayLength(teamNames);

      try {
        const {
          data: { sellSideMembers, buySideGroups },
        } = await dataRoomApi.members.inviteSellSideMembers(_id, {
          sellSideMembers: getArrayOfEmails(
            sellSideMembersVal,
            sellSideMembersUnsubmited,
          ),
          ...(hasTeamNames ? { teamNames } : {}),
        });

        setSellSideMembers(sellSideMembers);
        setBuySideGroups(buySideGroups);

        if (hasTeamNames) {
          setDataRoom((old) => ({
            ...old,
            teams: [
              ...(old.teams || []),
              ...teamNames.filter((el) => !old.teams?.includes(el)),
            ],
          }));
        }

        drawer?.setOpen(false);
      } catch (e) {
        popServerError(e);
      }
    },
    [
      _id,
      drawer,
      popServerError,
      setBuySideGroups,
      setSellSideMembers,
      setDataRoom,
    ],
  );

  if (doesntHaveAccess) return null;

  return (
    <SideDrawer
      ref={drawerRef}
      title={t('addNewMembersToSellSide')}
      className={classes}
    >
      {drawer?.open && (
        <Form
          onSubmit={onInviteMembers}
          initialValues={initialValues}
          render={({
            handleSubmit,
            submitting,
            form,
            values: {
              sellSideMembers,
              sellSideMembersUnsubmited,
              addNewTeam,
              teamNames,
            },
          }) => (
            <form onSubmit={handleSubmit}>
              <Field
                name="sellSideMembers"
                required
                label={t('emailTextareaFieldLabel')}
                component={TextareaEmailsField}
                description={t('weAreGoingToSendInvitationsViaEmail')}
              />

              {isAdmin && (
                <div className="row">
                  {canAddNewTeam && addNewTeam && (
                    <Field
                      name="teamNames"
                      component={TeamsField}
                      label={t('addToATeamOptional')}
                      description={t('teamsHint')}
                      maxNumberOfTeams={MAX_NUM_OF_TEAMS - teams?.length}
                      disabled={doesntHaveAccess}
                    />
                  )}

                  {!!teamsLength && !addNewTeam && (
                    <div className="column">
                      <Field
                        name="teamNames"
                        renderAsPortal
                        label={t('addToATeamOptional')}
                        component={ResolveMultiSelectField}
                        options={teamSelectOptions}
                      />
                      {renderTeams(teamNames)}
                    </div>
                  )}

                  {canAddNewTeam && !!teamsLength && (
                    <Button
                      variant="outline"
                      className="add-team"
                      {...(!addNewTeam && { icon: PlusIcon })}
                      onClick={() =>
                        form.batch(() => {
                          form.change('addNewTeam', !addNewTeam);
                          form.change('teamNames', []);
                        })
                      }
                    >
                      {t(addNewTeam ? 'existingTeam' : 'newTeam')}
                    </Button>
                  )}
                </div>
              )}

              <div className="actions">
                <Button
                  variant="link"
                  disabled={submitting}
                  onClick={() => drawer?.setOpen(false)}
                >
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={
                    submitting ||
                    (!getArrayLength(sellSideMembers) &&
                      !isEmail(sellSideMembersUnsubmited))
                  }
                >
                  {t('confirm')}
                </Button>
              </div>
            </form>
          )}
        />
      )}
    </SideDrawer>
  );
};

export default forwardRef(AddSellSideColleague);
